// src/App.js
import React from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  useLocation,
} from "react-router-dom";
import Navbar from "./navBars/Navbar";
import Home from "./components/Home";
import Login from "./components/Login";
import { AuthProvider } from "./providers/AuthProvider";
import VariableProvider from "./providers/VariableProvider";
import DataTables from "./components/DataTables";
import Weighting from "./components/Weighting";
import DataCheck from "./components/DataCheck";

import Banner from "./components/Banner";
import DataAnalysis from "./components/DataAnalysis";
import DashboardView from "./components/DashboardView";
import DashboardBuilder from "./components/DashboardBuilder";
import Sidebar from "./navBars/Sidebar";
import FreqProvider from "./providers/FreqProvider";
import SurveyElements from "./components/SurveyElements";
import SurveyBuilder from "./components/SurveyBuilder";
import QnrDesign from "./components/QnrDesign";
import Data from "./components/Data";
import CrosstabPage from "./components/CrosstabPage";
import PrivacyPolicy from "./components/PrivacyPolicy";
import TermsOfUse from "./components/TermsOfUse";
import SSOConsentNote from "./components/SSOConsentNote";
import AdminPanel from "./components/AdminPanel";
import { DragDropContext } from "@hello-pangea/dnd";
import VariableContext from "./contexts/VariableContext";
import { ToastProvider } from "./providers/ToastProvider";
import { ConfirmProvider } from "./components/ConfirmDialog";
import ErrorBoundary from "./components/ErrorBoundary";
import useVersionCheck from "./hooks/useVersionCheck";

// ── Theme ──────────────────────────────────────────────────────────
import { ThemeProvider } from "./contexts/ThemeProvider";   // 1
import ThemeSelector from "./components/ThemeSelector";    // 2
// ──────────────────────────────────────────────────────────────────

function App() {
  useVersionCheck();
  return (
    <ErrorBoundary>
      <ThemeProvider>                   {/* ← wraps everything so CSS vars apply globally */}
        <AuthProvider>
          <ToastProvider>
            <ConfirmProvider>
              <Router>
                <AppContent />
              </Router>
            </ConfirmProvider>
          </ToastProvider>
        </AuthProvider>
      </ThemeProvider>
    </ErrorBoundary>
  );
}

function AppContent() {
  const location = useLocation();
  const hideNavbarRoutes = ["/login", "/", "/privacy", "/terms", "/sso-consent"];
  const hasNavbar = !hideNavbarRoutes.includes(location.pathname);

  return (
    <VariableProvider>
      <FreqProvider>
        <>
          {hasNavbar && <Navbar />}
          <Routes>
            <Route path="/" element={<Login />} />
            <Route path="/login" element={<Login />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
            <Route path="/terms" element={<TermsOfUse />} />
            <Route path="/sso-consent" element={<SSOConsentNote />} />
            <Route path="/orgadmin" element={<AdminPanel />} />
            <Route path="/home" element={<Home />} />
            <Route
              path="/datatables"
              element={
                <WithSidebar hasNavbar={hasNavbar} component={DataTables} />
              }
            />
            <Route
              path="/weighting"
              element={
                <WithSidebar hasNavbar={hasNavbar} component={Weighting} />
              }
            />
            <Route
              path="/datacheck"
              element={
                <WithSidebar hasNavbar={hasNavbar} component={DataCheck} />
              }
            />
            <Route
              path="/banner"
              element={<WithSidebar hasNavbar={hasNavbar} component={Banner} />}
            />
            <Route
              path="/dataanalysis"
              element={
                <WithSidebar hasNavbar={hasNavbar} component={DataAnalysis} />
              }
            />
            <Route
              path="/data"
              element={<WithSidebar hasNavbar={hasNavbar} component={Data} />}
            />
            {/* <Route path="/crosstabmodel" element={<WithSidebar hasNavbar={hasNavbar} component={CrosstabPage} />} /> */}
            <Route path="/crosstabmodel" element={<CrosstabPage />} />
            <Route
              path="/dashboard"
              element={
                <WithSidebar
                  hasNavbar={hasNavbar}
                  component={DashboardBuilder}
                />
              }
            />
            <Route path="/dashboardbuilder" element={<DashboardBuilder />} />
            <Route path="/dashboardview" element={<DashboardView />} />
            <Route path="/surveyelement" element={<SurveyElements />} />
            <Route path="/qnrdesign" element={<QnrDesign />} />
            <Route
              path="/surveybuilder"
              element={
                <WithSidebar hasNavbar={hasNavbar} component={SurveyBuilder} />
              }
            />
          </Routes>

          {/* ── Theme picker — floats over every page ── */}
          <ThemeSelector position="bottom-right" />        {/* 3 */}
        </>
      </FreqProvider>
    </VariableProvider>
  );
}

function WithSidebar({ component: Component, hasNavbar }) {
  const { dndHandlers } = React.useContext(VariableContext);

  const handleDragEnd = (result) => {
    const { source, destination } = result;
    if (!destination) return;

    if (
      source.droppableId === "sidebar-questions" &&
      destination.droppableId === "sidebar-questions"
    ) {
      if (dndHandlers.sidebar) dndHandlers.sidebar(result);
    }

    if (
      source.droppableId === "sidebar-questions" &&
      (destination.droppableId === "rows" ||
        destination.droppableId === "columns")
    ) {
      if (dndHandlers.data) dndHandlers.data(result);
    }
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div
        className={`page-with-sidebar ${hasNavbar ? "" : "no-navbar"}`}
        style={{ display: "flex" }}
      >
        <Sidebar />
        <div className="main-content">
          <Component />
        </div>
      </div>
    </DragDropContext>
  );
}

export default App;